import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { throttle } from 'lodash';
import { map, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ColumnsDialogComponent } from 'app/components/shared/columns-dialog/columns-dialog.component';
import { ageGroup, csvColumns, dayNight, gender, months, races, weekDays } from 'app/constants/all-data.contants';
import { DeviceMaxSize, StorageKeys } from 'app/constants/common.constants';
import { CitationColumn, ICitationDetailsV2, ICitationTableColumns } from 'app/models/all-data.models';
import { Column } from 'app/models/columns-dialog.models';
import { Feature } from 'app/models/feature.models';
import { DayNight, FILTER_TYPE, IAutocompleteOption, IFilters, Month } from 'app/models/filter.models';
import { AllDataService } from 'app/services/all-data.service';
import { FeatureService } from 'app/services/feature.service';
import { FilterService } from 'app/services/filter.service';
import { HeaderService } from 'app/services/header.service';
import { getColumns, getDefaultAllDataColumns, sliceAndAddStar } from 'app/utils/all-data.utils';
import { getDialogConfig } from 'app/utils/columns-dialog.utils';
import { mapValuesToOptions } from 'app/utils/form.utils';
import { AllDataSource } from './all-data-source';
import { format } from 'date-fns';
import { getDayNight } from '../../../../utils/date.utils';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { AllDataDialogFilterComponent } from '../filter-dialog/button/all-data-filter-dialog-button.component';
import { AllDataPaginatorIntl } from './services/all-data-paginator-intl.service';

const mapOptionsToValues = (options: IAutocompleteOption[]): string[] =>
  options?.map(({ value }: { value: string }) => value);

@Component({
  selector: 'app-all-data-table',
  templateUrl: './all-data-table.component.html',
  styleUrls: ['./all-data-table.component.scss'],
  imports: [
    NgIf,
    MatProgressSpinner,
    MatProgressBar,
    MatTableModule,
    MatSortModule,
    NgFor,
    NgClass,
    AllDataDialogFilterComponent,
    MatPaginator,
    AsyncPipe,
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: AllDataPaginatorIntl }],
})
export class AllDataTableComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly Month = Month;
  private readonly subscriptions: Subscription = new Subscription();

  private internalDateState?: {
    dateFrom?: string;
    dateTo?: string;
  };

  private internalTimeState?: {
    hoursFrom?: string;
    hoursTo?: string;
    minutesFrom?: string;
    minutesTo?: string;
  };

  private internalReleaseYearState?: {
    dateFrom?: string | Date;
    dateTo?: string | Date;
  };

  private violationOptions = this.filterService
    .getViolationDescriptions()
    .pipe(map((violationDescriptions) => mapValuesToOptions(violationDescriptions)));

  private citationTypesOptions = this.filterService
    .getOffenceDescriptions()
    .pipe(map((offenseDescriptions) => mapValuesToOptions(offenseDescriptions)));

  private makeOptions = this.filterService.getVehicles().pipe(map((filters) => mapValuesToOptions(filters.makes)));

  private colorOptions = this.filterService.getVehicles().pipe(map((filters) => mapValuesToOptions(filters.colors)));

  private typeOptions = this.filterService.getVehicles().pipe(map((filters) => mapValuesToOptions(filters.types)));

  private modelOptions = this.filterService.getVehicles().pipe(map((filters) => mapValuesToOptions(filters.models)));

  private cityOptions = this.filterService.getAreas().pipe(map((filters) => mapValuesToOptions(filters.cities)));

  private zipCodeOptions = this.filterService.getAreas().pipe(map((filters) => mapValuesToOptions(filters.zipCodes)));

  columnsWithoutSort: CitationColumn[] = [
    CitationColumn.CitationType,
    CitationColumn.IssueType,
    CitationColumn.PhoneNumberCaptured,
    CitationColumn.OffenceCode,
  ];

  tableColumns: ICitationTableColumns[] = [
    {
      columnDef: CitationColumn.Id,
      header: 'Citation №',
      cell: ({ citationId }: ICitationDetailsV2) => citationId,
      filterType: FILTER_TYPE.TEXT,
      save: (value) => {
        const { citationFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.citationFilter = {
          ...citationFilter,
          id: value,
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.Month,
      header: 'Month',
      cell: ({ month }: ICitationDetailsV2) => month,
      filterType: FILTER_TYPE.SELECT,
      options: months,
      save: (value) => {
        const { dateFilter } = this.internalAppliedFilters;

        this.internalAppliedFilters.dateFilter = {
          ...dateFilter,
          months: value,
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.Date,
      header: 'Cit. date',
      cell: ({ date }: ICitationDetailsV2) => date ?? '',
      filterType: FILTER_TYPE.DATE,
      save: (value) => {
        const { dateFilter } = this.internalAppliedFilters;

        this.internalAppliedFilters.dateFilter = {
          ...dateFilter,
          fromDate: value?.dateFrom,
          toDate: value?.dateTo,
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.Time,
      header: 'Viol. time',
      cell: ({ dateTime }: ICitationDetailsV2) => {
        return format(new Date(dateTime), 'hh:mm a');
      }, // len(hh:mm) = 5
      filterType: FILTER_TYPE.TIME,
      sortDef: 'time',
      save: (value) => {
        if (!value) {
          const { dateFilter } = this.internalAppliedFilters;
          this.internalAppliedFilters.dateFilter = {
            ...dateFilter,
            fromTime: undefined,
            toTime: undefined,
          };
        } else {
          const { hoursFrom, hoursTo, minutesFrom, minutesTo } = value;

          if (hoursFrom && minutesFrom) {
            const { dateFilter } = this.internalAppliedFilters;
            this.internalAppliedFilters.dateFilter = {
              ...dateFilter,
              fromTime: `${hoursFrom}:${minutesFrom}`,
            };
          }

          if (hoursTo && minutesTo) {
            const { dateFilter } = this.internalAppliedFilters;
            this.internalAppliedFilters.dateFilter = {
              ...dateFilter,
              toTime: `${hoursTo}:${minutesTo}`,
            };
          }
        }

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.DayNight,
      header: 'Day / night',
      cell: ({ dayNight }: ICitationDetailsV2) => dayNight,
      filterType: FILTER_TYPE.SELECT,
      options: dayNight,
      isSingle: true,
      save: (value) => {
        let fromTime;
        let toTime;
        if (value === DayNight.DAY) {
          fromTime = '06:00';
          toTime = '18:00';
        } else if (value === DayNight.NIGHT) {
          fromTime = '18:00';
          toTime = '06:00';
        } else {
          fromTime = undefined;
          toTime = undefined;
        }

        const { dateFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.dateFilter = {
          ...dateFilter,
          fromTime: fromTime,
          toTime: toTime,
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.DayOfWeek,
      header: 'Day of week',
      cell: ({ dayOfWeek }: ICitationDetailsV2) => dayOfWeek,
      filterType: FILTER_TYPE.SELECT,
      options: weekDays,
      save: (value) => {
        const { dateFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.dateFilter = {
          ...dateFilter,
          daysOfWeek: value,
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.DriverLastName,
      header: 'Last Name',
      cell: ({ driver: { lastName } }: ICitationDetailsV2) => {
        if (!lastName) return '-';
        return sliceAndAddStar(lastName);
      },
      filterType: FILTER_TYPE.TEXT,
    },
    {
      columnDef: CitationColumn.DriverFirstName,
      header: 'First Name',
      cell: ({ driver: { firstName } }: ICitationDetailsV2) => {
        if (!firstName) return '-';
        return sliceAndAddStar(firstName);
      },
      filterType: FILTER_TYPE.TEXT,
    },
    {
      columnDef: CitationColumn.DriverMiddleName,
      header: 'Middle Name',
      cell: ({ driver: { middleName } }: ICitationDetailsV2) => {
        if (!middleName) return '-';
        return sliceAndAddStar(middleName);
      },
      filterType: FILTER_TYPE.TEXT,
    },
    {
      columnDef: CitationColumn.DateOfBirth,
      header: 'Cit. DOB',
      cell: ({ driver: { dateOfBirth } }: ICitationDetailsV2) => {
        if (!dateOfBirth) return '-';
        return sliceAndAddStar(dateOfBirth);
      },
      filterType: FILTER_TYPE.TEXT,
    },
    {
      columnDef: CitationColumn.PhoneNumberCaptured,
      header: 'Phone Number Captured',
      cell: ({ driver }: ICitationDetailsV2) => (driver.hasPhoneNumber ? 'Yes' : 'No'),
    },
    {
      columnDef: CitationColumn.Race,
      header: 'Race',
      cell: ({ driver: { race } }: ICitationDetailsV2) => {
        if (!race) return '-';
        return races.find((x) => x.value === race)?.viewValue || '';
      },
      filterType: FILTER_TYPE.SELECT,
      options: races,
      sortDef: 'raceEthnicity',
      save: (value) => {
        const { demographicFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.demographicFilter = {
          ...demographicFilter,
          races: value,
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.Sex,
      header: 'Sex',
      cell: ({ driver: { sex } }: ICitationDetailsV2) => {
        if (!sex) return '-';
        return sex;
      },
      filterType: FILTER_TYPE.SELECT,
      options: gender,
      isSingle: true,
      sortDef: 'gender',
      save: (value) => {
        const { demographicFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.demographicFilter = {
          ...demographicFilter,
          gender: value,
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.AgeGroup,
      header: 'Age Group',
      cell: ({ driver: { ageGroup } }: ICitationDetailsV2) => {
        if (!ageGroup) return '-';
        return ageGroup;
      },
      filterType: FILTER_TYPE.SELECT,
      options: ageGroup,
      isSingle: true,
      save: (value) => {
        const { demographicFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.demographicFilter = {
          ...demographicFilter,
          ageGroup: value,
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.ZipCode,
      header: 'Zip Code',
      cell: ({ location: { zipCode } }: ICitationDetailsV2) => {
        if (!zipCode) return '-';
        return zipCode;
      },
      filterType: FILTER_TYPE.AUTOCOMPLETE,
      options: this.zipCodeOptions,
      save: (value) => {
        const { locationFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.locationFilter = {
          ...locationFilter,
          zipCodes: mapOptionsToValues(value),
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.City,
      header: 'City',
      cell: ({ location: { city } }: ICitationDetailsV2) => {
        if (!city) return '-';
        return city;
      },
      filterType: FILTER_TYPE.AUTOCOMPLETE,
      options: this.cityOptions,
      save: (value) => {
        const { locationFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.locationFilter = {
          ...locationFilter,
          cities: mapOptionsToValues(value),
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.State,
      header: 'State',
      cell: ({ location: { state } }: ICitationDetailsV2) => {
        if (!state) return '-';
        return state;
      },
      filterType: FILTER_TYPE.TEXT,
    },
    {
      columnDef: CitationColumn.VehicleYear,
      header: 'Vehicle Year',
      cell: ({ vehicle: { year } }: ICitationDetailsV2) => {
        if (!year) return '-';
        return year;
      },
      filterType: FILTER_TYPE.YEAR,
      save: (value) => {
        const { vehicleFilter } = this.internalAppliedFilters;

        this.internalAppliedFilters.vehicleFilter = {
          ...vehicleFilter,
          releaseDateFrom: value?.dateFrom,
          releaseDateTo: value?.dateTo,
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.VehicleMake,
      header: 'Vehicle Make',
      cell: ({ vehicle: { make } }: ICitationDetailsV2) => {
        if (!make) return '-';
        return make;
      },
      filterType: FILTER_TYPE.AUTOCOMPLETE,
      options: this.makeOptions,
      save: (value) => {
        const { vehicleFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.vehicleFilter = {
          ...vehicleFilter,
          makes: mapOptionsToValues(value),
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.VehicleModel,
      header: 'Vehicle Model',
      cell: ({ vehicle: { model } }: ICitationDetailsV2) => {
        if (!model) return '-';
        return model;
      },
      filterType: FILTER_TYPE.AUTOCOMPLETE,
      options: this.modelOptions,
      save: (value) => {
        const { vehicleFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.vehicleFilter = {
          ...vehicleFilter,
          models: mapOptionsToValues(value),
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.VehicleType,
      header: 'Vehicle Type',
      cell: ({ vehicle: { type } }: ICitationDetailsV2) => {
        if (!type) return '-';
        return type;
      },
      filterType: FILTER_TYPE.AUTOCOMPLETE,
      options: this.typeOptions,
      save: (value) => {
        const { vehicleFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.vehicleFilter = {
          ...vehicleFilter,
          types: mapOptionsToValues(value),
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.VehicleColor,
      header: 'Vehicle Color',
      cell: ({ vehicle: { color } }: ICitationDetailsV2) => {
        if (!color) return '-';
        return color;
      },
      filterType: FILTER_TYPE.AUTOCOMPLETE,
      options: this.colorOptions,
      save: (value) => {
        const { vehicleFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.vehicleFilter = {
          ...vehicleFilter,
          colors: mapOptionsToValues(value),
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.LicPlate,
      header: 'Lic. Plate',
      cell: ({ vehicle: { licPlate } }: ICitationDetailsV2) => {
        if (!licPlate) return '-';
        return licPlate;
      },
      filterType: FILTER_TYPE.TEXT,
    },
    {
      columnDef: CitationColumn.VehicleState,
      header: 'Lic. Plate',
      cell: ({ vehicle: { state } }: ICitationDetailsV2) => {
        if (!state) return '-';
        return state;
      },
      filterType: FILTER_TYPE.TEXT,
    },
    {
      columnDef: CitationColumn.Officer,
      header: 'Officer',
      cell: ({ officer: { firstName, lastName, middleName } }: ICitationDetailsV2) => {
        return sliceAndAddStar([firstName, lastName, middleName].filter((x) => x).join(' '), 20);
      },
      filterType: FILTER_TYPE.TEXT,
    },
    {
      columnDef: CitationColumn.OfficerId,
      header: 'Officer Id',
      cell: ({ officer: { id } }: ICitationDetailsV2) => {
        if (!id) return '';
        return `${id}`;
      },
      filterType: FILTER_TYPE.TEXT,
    },
    {
      columnDef: CitationColumn.IssueType,
      header: 'Citation Type',
      cell: ({ issueType }: ICitationDetailsV2) => {
        if (!issueType) return '-';
        return issueType;
      },
    },
    {
      columnDef: CitationColumn.CitationType,
      header: 'Citation Name',
      cell: ({ citationType }: ICitationDetailsV2) => {
        if (!citationType) return '-';
        return citationType;
      },
      filterType: FILTER_TYPE.AUTOCOMPLETE,
      options: this.citationTypesOptions,
      save: (value) => {
        const { violationFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.violationFilter = {
          ...violationFilter,
          names: mapOptionsToValues(value),
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.OffenceCode,
      header: 'Offense Code',
      cell: ({ offenceCode }: ICitationDetailsV2) => {
        if (!offenceCode) return '-';
        return offenceCode;
      },
      filterType: FILTER_TYPE.AUTOCOMPLETE,
      options: this.violationOptions,
      save: (value) => {
        const { violationFilter } = this.internalAppliedFilters;
        this.internalAppliedFilters.violationFilter = {
          ...violationFilter,
          policeCodes: mapOptionsToValues(value),
        };

        this.filterService.applyAllFilters(this.internalAppliedFilters);
      },
    },
    {
      columnDef: CitationColumn.Department,
      header: 'Department',
      sortDef: 'departmentId',
      cell: ({ department }: ICitationDetailsV2) => {
        if (!department) return '-';
        return department.name;
      },
    },
  ];

  columns: Column[] = [];
  defaultColumns: CitationColumn[] = [];
  displayedColumns: CitationColumn[] = [];

  internalAppliedFilters: IFilters = {};

  displayedTableColumns: ICitationTableColumns[] = this.getDisplayedTableColumns();

  dataSource!: AllDataSource;

  @ViewChild('paginator') paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  loading = false;

  private columnPanelSubscription!: Subscription;
  private exportSub!: Subscription;
  private dialogRef?: MatDialogRef<ColumnsDialogComponent>;

  constructor(
    private allDataService: AllDataService,
    private selectColumnsDialog: MatDialog,
    public dialog: MatDialog,
    private headerService: HeaderService,
    private filterService: FilterService,
    private featureService: FeatureService,
  ) {
    const isPersonalInfoEnabled = this.featureService.isFeatureEnabled(Feature.PERSONAL_INFO);
    this.columns = getColumns(isPersonalInfoEnabled);
    this.defaultColumns = getDefaultAllDataColumns(isPersonalInfoEnabled);
    this.displayedColumns = this.getSelectedColumns();
  }

  getSelectedColumns() {
    const selectedColumns = sessionStorage.getItem(StorageKeys.ALL_DATA_SELECTED_COLUMNS);
    return selectedColumns ? JSON.parse(selectedColumns) : this.defaultColumns;
  }

  getParams(): HttpParams {
    let result = new HttpParams()
      .append('size', this.paginator?.pageSize || 20)
      .append('page', this.paginator?.pageIndex || 0);

    if (this.sort?.active) {
      for (const sort of this.sort.active.split(',')) {
        result = result.append('sort', `${sort || ''},${this.sort.direction}`);
      }
    }

    return result;
  }

  public getFilter(name: CitationColumn) {
    switch (name) {
      case CitationColumn.OffenceCode: {
        return mapValuesToOptions(this.internalAppliedFilters.violationFilter?.policeCodes ?? []);
      }
      case CitationColumn.CitationType: {
        return mapValuesToOptions(this.internalAppliedFilters.violationFilter?.names ?? []);
      }
      case CitationColumn.Id: {
        return this.internalAppliedFilters.citationFilter?.id;
      }
      case CitationColumn.Month: {
        return this.internalAppliedFilters.dateFilter?.months;
      }
      case CitationColumn.DayOfWeek: {
        return this.internalAppliedFilters.dateFilter?.daysOfWeek;
      }
      case CitationColumn.DayNight: {
        return getDayNight(
          this.internalTimeState?.hoursFrom,
          this.internalTimeState?.minutesFrom,
          this.internalTimeState?.hoursTo,
          this.internalTimeState?.minutesTo,
        );
      }
      case CitationColumn.Race: {
        return this.internalAppliedFilters.demographicFilter?.races;
      }
      case CitationColumn.Sex: {
        return this.internalAppliedFilters.demographicFilter?.gender;
      }
      case CitationColumn.AgeGroup: {
        return this.internalAppliedFilters.demographicFilter?.ageGroup;
      }
      case CitationColumn.Time: {
        return this.internalTimeState;
      }
      case CitationColumn.Date: {
        return this.internalDateState;
      }
      case CitationColumn.VehicleMake: {
        return mapValuesToOptions(this.internalAppliedFilters.vehicleFilter?.makes ?? []);
      }
      case CitationColumn.VehicleColor: {
        return mapValuesToOptions(this.internalAppliedFilters.vehicleFilter?.colors ?? []);
      }
      case CitationColumn.VehicleModel: {
        return mapValuesToOptions(this.internalAppliedFilters.vehicleFilter?.models ?? []);
      }
      case CitationColumn.VehicleType: {
        return mapValuesToOptions(this.internalAppliedFilters.vehicleFilter?.types ?? []);
      }
      case CitationColumn.City: {
        return mapValuesToOptions(this.internalAppliedFilters.locationFilter?.cities ?? []);
      }
      case CitationColumn.ZipCode: {
        return mapValuesToOptions(this.internalAppliedFilters.locationFilter?.zipCodes ?? []);
      }
      case CitationColumn.VehicleYear: {
        return this.internalReleaseYearState;
      }
    }
    return '';
  }

  ngOnInit() {
    this.dataSource = new AllDataSource(this.allDataService);
    const { filters } = this.filterService;

    this.dataSource.loadData(filters, this.getParams(), this.displayedColumns);
    this.initFilters({ ...filters });

    const filtersSub = this.filterService.applyFiltersChange.subscribe(() => {
      this.initFilters(this.filterService.appliedFilters);
      this.paginator.pageIndex = 0;

      this.loadData();
    });

    this.subscriptions.add(filtersSub);

    this.columnPanelSubscription = this.headerService.toggleColumnPanelChange.subscribe((isOpened: boolean) => {
      if (isOpened) {
        this.openEditColumnsPanel();
      }
    });
    this.exportSub = this.headerService.exportChange.subscribe(() => {
      const { filters } = this.filterService;

      this.allDataService
        .exportAll(filters, this.getParams(), this.displayedColumns.map((el) => csvColumns[el]).filter(Boolean))
        .subscribe((e) => {
          this.download('all-data.csv', e);
        });
    });

    this.dataSource.totalElements$
      .pipe(
        tap((count) => {
          if (!this.paginator) return;
          this.paginator.length = count;
        }),
      )
      .subscribe();
    this.dataSource.size$
      .pipe(
        tap((size) => {
          if (!this.paginator) return;
          this.paginator.pageSize = size;
        }),
      )
      .subscribe();
  }

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  download(filename: string, text: any) {
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
    element.setAttribute('download', filename);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  }

  ngOnDestroy() {
    this.columnPanelSubscription.unsubscribe();
    this.exportSub.unsubscribe();
    !!this.subscriptions && this.subscriptions.unsubscribe();
  }

  ngAfterViewInit() {
    this.paginator.page
      .pipe(
        tap(() => {
          this.loadData();
        }),
      )
      .subscribe();
  }

  loadData() {
    const { filters } = this.filterService;
    this.dataSource.loadData(filters, this.getParams(), this.displayedColumns);
  }

  sortData(): void {
    this.loadData();
  }

  openEditColumnsPanel() {
    this.dialogRef = this.selectColumnsDialog.open(
      ColumnsDialogComponent,
      getDialogConfig({
        columns: this.columns,
        defaultColumns: this.defaultColumns,
        displayedColumns: this.displayedColumns,
      }),
    );

    this.dialogRef.afterClosed().subscribe((result) => {
      if (result != null) {
        sessionStorage.setItem(StorageKeys.ALL_DATA_SELECTED_COLUMNS, JSON.stringify(result));
      }
      this.headerService.toggleColumnPanel(false);
      if (result?.length) {
        this.updateEnabledColumns(result);
        this.loadData();
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize = throttle(() => {
    if (this.dialogRef && this.dialogRef.getState() === MatDialogState.OPEN) {
      this.dialogRef.updateSize('100%', window.innerWidth <= DeviceMaxSize.TABLET_HORIZONTAL ? '100%' : 'auto');
    }
  }, 100);

  getDisplayedTableColumns() {
    return this.tableColumns.filter((column) => this.displayedColumns.includes(column.columnDef));
  }

  updateEnabledColumns(newEnabledColumns: CitationColumn[]) {
    this.displayedColumns = [...newEnabledColumns];
    this.displayedTableColumns = this.getDisplayedTableColumns();
  }

  isFilterEmpty(column: CitationColumn): boolean {
    const value = this.getFilter(column);
    if (!value) return false;
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'object') {
      return Object.values(value).some((val) => val);
    }
    return value.length > 0;
  }

  private initFilters(filters: IFilters) {
    this.internalAppliedFilters = filters;

    const fromTime = filters.dateFilter?.fromTime;
    const [hoursFrom, minutesFrom] = fromTime ? fromTime.split(':') : [undefined, undefined];

    const toTime = filters.dateFilter?.toTime;
    const [hoursTo, minutesTo] = toTime ? toTime.split(':') : [undefined, undefined];

    this.internalDateState = {
      dateFrom: this.internalAppliedFilters.dateFilter?.fromDate,
      dateTo: this.internalAppliedFilters.dateFilter?.toDate,
    };

    this.internalTimeState = {
      hoursFrom,
      hoursTo,
      minutesFrom,
      minutesTo,
    };

    this.internalReleaseYearState = {
      dateFrom: this.internalAppliedFilters.vehicleFilter?.releaseDateFrom,
      dateTo: this.internalAppliedFilters.vehicleFilter?.releaseDateTo,
    };
  }
}
